import { useState } from 'react'
import type { Reminder } from '@/services/api'
import Button from './Button'
import styles from './ReminderModal.module.css'
import Icon from '@/components/ui/Icon'

interface ReminderModalProps {
  reminders: Reminder[]
  onAddNew: () => void
  onEdit: (reminder: Reminder) => void
  onDelete: (reminderId: string) => void
  onClose: () => void
}

function formatReminderTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const isToday = date.toDateString() === now.toDateString()
  const isTomorrow = date.toDateString() === tomorrow.toDateString()

  const timeStr = date.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  if (isToday) {
    return `Today at ${timeStr}`
  } else if (isTomorrow) {
    return `Tomorrow at ${timeStr}`
  } else {
    return `${date.toLocaleDateString('en-IN', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })} at ${timeStr}`
  }
}

function getRepeatLabel(reminder: Reminder): string | null {
  if (!reminder.repeat_config || reminder.repeat_config.type === 'once') {
    return null
  }

  switch (reminder.repeat_config.type) {
    case 'daily':
      return 'Daily'
    case 'weekly':
      return 'Weekly'
    case 'custom':
      if (reminder.repeat_config.days && reminder.repeat_config.days.length > 0) {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        const selectedDays = reminder.repeat_config.days
          .sort((a, b) => a - b)
          .map(d => dayNames[d])
          .join(', ')
        return selectedDays
      }
      return 'Custom'
    default:
      return null
  }
}

export default function ReminderModal({
  reminders,
  onAddNew,
  onEdit,
  onDelete,
  onClose,
}: ReminderModalProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleDelete = async (reminderId: string) => {
    setDeletingId(reminderId)
    try {
      await onDelete(reminderId)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className={styles.modal}>
      <div className={styles.header}>
        <h3 className={styles.title}>Reminders</h3>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>

      <div className={styles.content}>
        {reminders.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><Icon name="bell" size={22} /></div>
            <p className={styles.emptyText}>No reminders set</p>
            <p className={styles.emptySubtext}>
              Schedule this activity to build a routine
            </p>
          </div>
        ) : (
          <ul className={styles.reminderList}>
            {reminders.map((reminder) => {
              const repeatLabel = getRepeatLabel(reminder)
              return (
                <li key={reminder.id} className={styles.reminderItem}>
                  <div className={styles.reminderInfo}>
                    <span className={styles.reminderTime}>
                      {formatReminderTime(reminder.scheduled_at)}
                    </span>
                    {repeatLabel && (
                      <span className={styles.repeatBadge}>{repeatLabel}</span>
                    )}
                  </div>
                  <div className={styles.reminderActions}>
                    <button
                      className={styles.editButton}
                      onClick={() => onEdit(reminder)}
                      aria-label="Edit reminder"
                    >
                      <Icon name="pencil" size={22} />
                    </button>
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDelete(reminder.id)}
                      disabled={deletingId === reminder.id}
                      aria-label="Delete reminder"
                    >
                      {deletingId === reminder.id ? '...' : ''}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          variant="primary"
          fullWidth
          onClick={onAddNew}
        >
          + Add another reminder
        </Button>
      </div>
    </div>
  )
}
